/**
 * accountModules.service.ts
 *
 * Per-account module access, managed by super admins in Prospera-Corp-App's
 * Module Settings screen. `enabled: false` means the whole module is turned
 * off for this account; `disabledSections` lists sub-section slugs the
 * account must not see (slugs match the route segment under the module,
 * e.g. `push-campaigns` → /marketing/push-campaigns).
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AccountModule {
  id: number;
  /** Lowercased module title, e.g. "marketing" — the lookup key. */
  key: string;
  title: string;
  enabled: boolean;
  /**
   * An explicitly ACTIVE aa_accounts_modules row exists. `enabled` fails open
   * (no rows = true); `explicit` is the fail-closed signal used by opt-in
   * surfaces such as the Facilities nav entry. Optional until the backend
   * change ships everywhere.
   */
  explicit?: boolean;
  disabledSections: string[];
  /**
   * Opt-in features switched on in Corp-App for this module (off unless
   * listed), e.g. `meta-ads` under Marketing. Optional until the backend
   * change ships everywhere.
   */
  enabledFeatures?: string[];
}

/**
 * An Account's level for one Admin page, set by super admins (Corp-App and
 * corp-dashboard Admin Settings). Editable is the full page, Locked is
 * read-only, Hidden is out of the menu and its URL refused.
 */
export type AdminPageLevel = 'editable' | 'locked' | 'hidden';

/**
 * Admin page key (the catalog key in api/resources/admin-pages.json, e.g.
 * `products`, `integrations/ai`) → level. A missing key means Hidden.
 */
export type AdminPageLevelMap = Readonly<Record<string, AdminPageLevel>>;

export interface AccountModulesData {
  modules: AccountModule[];
  /**
   * The Account's Admin page levels. Only a real `/account/modules` response
   * carries it; the persisted authStore seed (login / me payloads) never does,
   * and the UI trusts it only from a fresh fetch (see useAdminPageLevels).
   */
  adminPageLevels?: AdminPageLevelMap;
}

export async function getAccountModules(): Promise<AccountModulesData> {
  return apiClient
    .get<ApiResponse<AccountModulesData>>('/account/modules')
    .then((r) => r.data.data);
}
